/**
 * Mobile size sheet.
 *
 * Wraps the theme's variant picker rather than replacing it. The real control
 * (a <select> under option_style "drop_down", radio inputs under
 * "text_underline", "buttons" and "radio") stays in the DOM and keeps owning
 * variant state. This component writes to it and dispatches a change event, so
 * Alpine's x-model and the theme's optionChange() continue to drive price,
 * availability and form submission.
 *
 * Everything visual is gated to mobile in CSS. Desktop is untouched.
 */
class SizeSheet extends HTMLElement {
  #keyboardNav = false;
  #onKeydown = null;
  #onPointerdown = null;
  #pending = null;
  #chosen = false;
  #closing = false;
  #onOptionChange = null;

  connectedCallback() {
    this.trigger = this.querySelector('[data-size-sheet-trigger]');
    this.dialog = this.querySelector('[data-size-sheet-dialog]');
    this.label = this.querySelector('[data-size-sheet-label]');
    this.confirm = this.querySelector('[data-size-sheet-confirm]');
    this.options = [...this.querySelectorAll('[data-size-sheet-option]')];
    this.guide = this.querySelector('[data-size-sheet-guide]');
    this.guideDialog = this.querySelector('[data-size-guide-dialog]');
    this.guidePanel = this.guideDialog?.querySelector('.size-sheet__panel');
    this.guideCards = this.querySelector('[data-size-guide-cards]');

    this.optionNames = this.#readJSON('[data-option-names]', 'optionNames') ?? [];
    this.sizeIndex = Number(this.dataset.sizeIndex);
    this.variants = this.#readJSON('[data-size-sheet-variants]') ?? [];

    this.control = this.#findControl();
    if (!this.control || !this.dialog) return;

    // Mark the option's fieldset so CSS can hide it on mobile only. Each option
    // gets its own fieldset, so colour swatches stay visible.
    this.control.fieldset?.classList.add('product-variant-picker-block--sheet-driven');
    this.classList.add('size-sheet--ready');

    this.panel = this.querySelector('.size-sheet__panel');

    this.trigger.addEventListener('click', () => this.#open());
    this.confirm.addEventListener('click', this.#commit.bind(this));
    this.dialog.addEventListener('click', this.#backdropClose.bind(this));
    // Escape would otherwise close instantly, skipping the exit.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#requestClose();
    });

    for (const option of this.options) {
      option.addEventListener('click', () => this.#choose(option));
    }

    // Which sizes are buyable depends on the other options - colour, here - so
    // the sheet has to follow the shopper's swatch. The theme's swatches are
    // real radio inputs, so their change event bubbles to the form container.
    // Our own writes to the size control are skipped: changing size cannot
    // change which sizes exist, and repainting mid-commit would fight #commit.
    this.form = this.closest('[data-product-form-container]');
    this.#onOptionChange = (event) => {
      const target = event.target;
      if (!target?.matches?.('[data-single-option-selector]')) return;
      if (target.name === `options[${this.dataset.optionName}]`) return;
      this.#refreshAvailability();
    };
    this.form?.addEventListener('change', this.#onOptionChange);

    // Liquid renders the state for the variant the page loaded with, so this
    // is a no-op on first paint. It earns its keep when the shopper arrives on
    // a ?variant= URL the markup was not rendered for.
    this.#refreshAvailability();

    this.#wireSizeGuide();
    this.#wireDrag(this.dialog, this.panel, () => this.#requestClose());

    // Which input moved focus decides whether restoring it should draw a ring.
    this.#onKeydown = (event) => {
      if (event.key === 'Tab') this.#keyboardNav = true;
    };
    this.#onPointerdown = () => {
      this.#keyboardNav = false;
    };
    document.addEventListener('keydown', this.#onKeydown, true);
    document.addEventListener('pointerdown', this.#onPointerdown, true);
  }

  /**
   * Swipe-to-dismiss from the grab handle and the heading row.
   *
   * Deliberately not the whole panel: the guide's card list scrolls, and a
   * drag starting there has to stay a scroll. Restricting the zone also keeps
   * a stray downward drag from swallowing a tap on a size.
   *
   * @param {HTMLDialogElement} dialog
   * @param {HTMLElement} panel
   * @param {() => void} onDismiss - what dismissal means for this sheet; the
   *   size sheet closes, the guide pops back to the size sheet.
   * @returns {void}
   */
  #wireDrag(dialog, panel, onDismiss) {
    const zones = panel.querySelectorAll('[data-size-sheet-drag]');
    if (zones.length === 0) return;

    let startY = 0;
    let distance = 0;
    let startedAt = 0;
    let dragging = false;

    const down = (event) => {
      // Let the controls in the heading row behave like controls.
      if (!dialog.open || event.button > 0 || event.target.closest('button, a')) return;
      dragging = true;
      startY = event.clientY;
      distance = 0;
      startedAt = event.timeStamp;
      panel.style.transition = 'none';
      event.currentTarget.setPointerCapture?.(event.pointerId);
    };

    const move = (event) => {
      if (!dragging) return;
      // Downward only - dragging up must not lift the sheet off the bottom.
      distance = Math.max(0, event.clientY - startY);
      panel.style.transform = `translateY(${distance}px)`;
    };

    const up = (event) => {
      if (!dragging) return;
      dragging = false;
      event.currentTarget.releasePointerCapture?.(event.pointerId);

      const elapsed = Math.max(1, event.timeStamp - startedAt);
      const flicked = distance / elapsed > 0.5;          // px per ms
      const dragged = distance > panel.offsetHeight * 0.25;

      // Re-enable the transition before clearing the inline transform, so the
      // panel carries on from where the finger left it rather than jumping.
      panel.style.transition = '';
      panel.style.transform = '';
      if (flicked || dragged) onDismiss();
    };

    for (const zone of zones) {
      zone.addEventListener('pointerdown', down);
      zone.addEventListener('pointermove', move);
      zone.addEventListener('pointerup', up);
      zone.addEventListener('pointercancel', up);
    }
  }

  /**
   * The guide is a second bottom sheet that replaces the size sheet, so the
   * two share one motion language instead of handing off to the theme's
   * desktop-shaped popup modal.
   * @returns {void}
   */
  #wireSizeGuide() {
    if (!this.guide || !this.guideDialog) return;

    this.guide.addEventListener('click', () => {
      // The size sheet deliberately stays open underneath. Both are modal
      // <dialog>s, so the guide is pushed onto the top layer above it and the
      // browser makes the sheet below inert on its own. Dismissing the guide
      // then reveals the sheet already in place, instead of closing one sheet
      // and sliding another back up for every visit to the chart.
      this.#openGuide();
    });

    // Escape would otherwise close outright, skipping both the exit and the
    // return to the size sheet.
    this.guideDialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#closeGuide();
    });

    this.guideDialog.addEventListener('click', (event) => {
      // showModal() fills the viewport, so scrim taps land on the dialog.
      if (event.target === this.guideDialog) this.#closeGuide();
    });

    this.#wireDrag(this.guideDialog, this.guidePanel, () => this.#closeGuide());
  }

  /**
   * Only the guide closes - the size sheet was never dismissed, so it is
   * already there, with the shopper's size still selected. Focus goes back to
   * the control they opened the guide from.
   * @returns {Promise<void>}
   */
  #closeGuide() {
    return this.#requestClose(this.guideDialog, this.guidePanel, this.guide);
  }

  /** @returns {void} */
  #openGuide() {
    this.#buildGuide();
    this.guideDialog.showModal();
  }

  /**
   * Turns the metaobject's pipe-delimited rows into one card per size, which
   * reads far better on a narrow screen than a table that has to scroll
   * sideways. Mirrors the format the theme's own renderer expects - a header
   * row, body rows, and an optional trailing "Notes:" block - so a chart
   * written for one renders correctly in the other.
   * @returns {void}
   */
  #buildGuide() {
    if (!this.guideCards || this.guideCards.dataset.built === 'true') return;
    const source = this.querySelector('[data-size-guide-source]');
    const lines = (source?.innerText ?? '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

    const tableLines = [];
    const noteLines = [];
    let inNotes = false;
    for (const line of lines) {
      if (/^notes:?$/i.test(line)) {
        inNotes = true;
      } else if (inNotes) {
        noteLines.push(line.replace(/^[-\u2022]\s*/, ''));
      } else if (line.includes('|')) {
        tableLines.push(line);
      }
    }

    const rows = tableLines
      .map((line) => line.split('|').map((cell) => cell.trim()).filter(Boolean))
      // drop markdown separator rows like |---|---|
      .filter((row) => row.length > 0 && !row.every((cell) => /^:?-{2,}:?$/.test(cell)));

    if (rows.length < 2) return;

    const [header, ...body] = rows;
    const frag = document.createDocumentFragment();

    header.slice(1).forEach((size, index) => {
      const card = document.createElement('section');
      card.className = 'size-guide__card';

      const label = document.createElement('h3');
      label.className = 'size-guide__card-title u-caps';
      label.textContent = size;
      card.append(label);

      for (const row of body) {
        const line = document.createElement('div');
        line.className = 'size-guide__row';
        const key = document.createElement('span');
        key.className = 'size-guide__key';
        key.textContent = row[0] ?? '';
        const val = document.createElement('span');
        val.className = 'size-guide__val';
        val.textContent = row[index + 1] ?? '\u2014';
        line.append(key, val);
        card.append(line);
      }
      frag.append(card);
    });

    if (noteLines.length > 0) {
      const notes = document.createElement('div');
      notes.className = 'size-guide__notes';
      const heading = document.createElement('p');
      heading.className = 'size-guide__notes-title u-caps';
      heading.textContent = 'Notes';
      const list = document.createElement('ul');
      for (const note of noteLines) {
        const item = document.createElement('li');
        item.textContent = note;
        list.append(item);
      }
      notes.append(heading, list);
      frag.append(notes);
    }

    this.guideCards.replaceChildren(frag);
    this.guideCards.dataset.built = 'true';
    // Only hide the raw source once the parse has actually produced cards, so
    // a chart in an unexpected shape still reads as plain text.
    if (source) source.hidden = true;
  }

  disconnectedCallback() {
    if (this.#onOptionChange) this.form?.removeEventListener('change', this.#onOptionChange);
    if (this.dialog?.open) this.dialog.close();  // teardown: no animation
    if (this.#onKeydown) document.removeEventListener('keydown', this.#onKeydown, true);
    if (this.#onPointerdown) document.removeEventListener('pointerdown', this.#onPointerdown, true);
  }

  /**
   * @returns {{kind: 'select'|'radio', select?: HTMLSelectElement,
   *            radios?: HTMLInputElement[], fieldset: HTMLElement|null}|null}
   */
  #findControl() {
    const name = `options[${this.dataset.optionName}]`;
    const root = this.closest('[data-product-form-container]') ?? document;

    for (const select of root.querySelectorAll('select[data-single-option-selector]')) {
      if (select.name === name) {
        return { kind: 'select', select, fieldset: select.closest('.product-variant-picker-block') };
      }
    }

    const radios = [...root.querySelectorAll('input[type="radio"][data-single-option-selector]')]
      .filter((input) => input.name === name);
    if (radios.length > 0) {
      return { kind: 'radio', radios, fieldset: radios[0].closest('.product-variant-picker-block') };
    }

    return null;
  }

  /** @returns {string} */
  #currentValue() {
    if (this.control.kind === 'select') return this.control.select.value;
    return this.control.radios.find((input) => input.checked)?.value ?? '';
  }

  /** @param {string} value */
  #setValue(value) {
    if (this.control.kind === 'select') {
      this.control.select.value = value;
      this.control.select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const target = this.control.radios.find((input) => input.value === value);
    if (!target) return;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #open() {
    this.#pending = this.#currentValue();
    this.#paint(this.#pending);
    this.dialog.showModal();
  }

  #backdropClose(event) {
    // showModal() fills the viewport, so clicks outside the panel land on the
    // dialog element itself.
    if (event.target === this.dialog) this.#requestClose();
  }

  /**
   * Plays the exit, then closes.
   *
   * CSS alone cannot do this reliably: the dialog only stays visible during an
   * exit if the browser can transition the `overlay` property, which WebKit
   * does not. Chrome on iOS is WebKit, so the sheet popped out of existence
   * there while animating correctly in Chromium. Driving the exit from a class
   * and closing afterwards works in every engine.
   *
   * @returns {void}
   */
  #requestClose(dialog = this.dialog, panel = this.panel, refocus = this.trigger) {
    if (this.#closing || !dialog.open) return Promise.resolve();
    this.#closing = true;
    dialog.classList.add('size-sheet__dialog--closing');

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        panel?.removeEventListener('transitionend', onEnd);
        dialog.classList.remove('size-sheet__dialog--closing');
        dialog.close();
        this.#closing = false;
        // Restoring focus is for keyboard users, who need somewhere to carry
        // on from. After a tap it only lights the trigger up with a ring the
        // shopper never asked for, so leave focus where it fell.
        if (this.#keyboardNav) refocus?.focus();
        resolve();
      };
      const onEnd = (event) => {
        if (event.propertyName === 'transform') finish();
      };

      panel?.addEventListener('transitionend', onEnd);
      // Ceiling, so a skipped or interrupted transition can never strand the
      // sheet open.
      setTimeout(finish, 400);
    });
  }

  /** @param {HTMLButtonElement} option */
  #choose(option) {
    this.#pending = option.dataset.value;
    this.#paint(this.#pending);
  }

  /**
   * @param {string} selector
   * @param {string} [attr] read from this dataset key instead of textContent
   * @returns {any}
   */
  #readJSON(selector, attr) {
    const raw = attr ? this.dataset[attr] : this.querySelector(selector)?.textContent;
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      // Availability then stays exactly as Liquid rendered it, which is right
      // for the loaded colour. Better a stale sheet than a blank one.
      return null;
    }
  }

  /**
   * The shopper's current choice for one option, read from the theme's own
   * control so there is a single source of truth.
   * @param {number} index
   * @returns {string|null}
   */
  #selectionFor(index) {
    const name = `options[${this.optionNames[index]}]`;
    const root = this.form ?? document;

    for (const select of root.querySelectorAll('select[data-single-option-selector]')) {
      if (select.name === name) return select.value;
    }
    for (const radio of root.querySelectorAll('input[type="radio"][data-single-option-selector]')) {
      if (radio.name === name && radio.checked) return radio.value;
    }
    return null;
  }

  /**
   * Re-derives every row against the colour on screen. A size with no matching
   * variant at all counts as unavailable rather than buyable.
   * @returns {void}
   */
  #refreshAvailability() {
    if (this.variants.length === 0 || !Number.isInteger(this.sizeIndex)) return;

    const selections = this.optionNames.map(
      (_, index) => (index === this.sizeIndex ? null : this.#selectionFor(index))
    );

    for (const option of this.options) {
      const variant = this.variants.find(
        (candidate) =>
          candidate.options[this.sizeIndex] === option.dataset.value &&
          candidate.options.every(
            (value, index) => index === this.sizeIndex || value === selections[index]
          )
      );
      const available = variant?.available === true;
      option.dataset.available = String(available);
      option.classList.toggle('size-sheet__option--out', !available);
      this.#paintNote(option, available, variant?.quantity ?? 0);
    }

    // The confirm button's wording comes off the same state, so it has to move
    // with it - otherwise a sheet left open over a colour switch still offers
    // to add a size that just sold out.
    if (this.#pending !== null) this.#paint(this.#pending);
  }

  /**
   * @param {HTMLButtonElement} option
   * @param {boolean} available
   * @param {number} quantity
   */
  #paintNote(option, available, quantity) {
    const note = option.querySelector('[data-size-sheet-note]');
    if (!note) return;

    note.classList.remove('size-sheet__note--out', 'size-sheet__note--low');
    if (!available) {
      note.textContent = 'Notify me';
      note.classList.add('size-sheet__note--out');
    } else if (this.dataset.preorder !== 'true' && quantity > 0 && quantity <= 2) {
      note.textContent = `Only ${quantity} left`;
      note.classList.add('size-sheet__note--low');
    } else {
      note.textContent = '';
    }
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #isAvailable(value) {
    const option = this.options.find((o) => o.dataset.value === value);
    return option ? option.dataset.available === 'true' : false;
  }

  /** @param {string} value */
  #paint(value) {
    for (const option of this.options) {
      option.setAttribute('aria-pressed', String(option.dataset.value === value));
    }
    const chosen = this.options.find((o) => o.dataset.value === value);
    this.confirm.disabled = !chosen;
    if (!chosen) {
      this.confirm.textContent = 'Select a size';
    } else if (this.#isAvailable(value)) {
      const verb = this.dataset.preorder === 'true' ? 'Pre-order' : 'Add to Cart';
      this.confirm.textContent = `${verb} — ${chosen.dataset.value}`;
    } else {
      this.confirm.textContent = 'Notify me when available';
    }
  }

  async #commit() {
    if (!this.#pending) return;

    const form = this.closest('[data-product-form-container]');
    const variantInput = form?.querySelector('input[name="id"], select[name="id"]');
    const previousVariant = variantInput?.value;
    const variantWillChange = this.#pending !== this.#currentValue();

    this.#setValue(this.#pending);
    this.#chosen = true;
    // Shopify preselects a variant, so the trigger keeps its call to action
    // until the shopper has actually chosen something.
    this.label.textContent = `${this.dataset.optionName} ${this.#pending}`;
    const closed = this.#requestClose();

    // The theme updates the form's variant id reactively through Alpine, which
    // lands a tick or more after the change event. Submitting immediately adds
    // the PREVIOUS variant to the cart, so wait for the id to catch up first.
    if (variantWillChange && variantInput) {
      await this.#awaitVariant(variantInput, previousVariant);
    }

    if (!this.#isAvailable(this.#pending)) {
      // Sold out: STOQ renders its notify form for whichever variant is
      // selected, so the variant change above is what surfaces it. Bring it
      // into view rather than adding an unbuyable item to the cart.
      //
      // Wait for the sheet to finish sliding out first. Scrolling while it is
      // still on screen puts two movements on top of each other and reads as
      // janky; sequenced, it is one motion handing off to the next.
      await closed;
      this.#revealNotifyForm();
      return;
    }

    const addToCart = form?.querySelector(
      '[data-add-to-cart], button[name="add"], [type="submit"]'
    );
    addToCart?.click();
  }

  /**
   * STOQ injects its form asynchronously after the variant changes, so poll
   * briefly for it rather than assuming it is already there.
   * @returns {void}
   */
  #revealNotifyForm() {
    const started = performance.now();
    const look = () => {
      const form = document.querySelector('.restock-rocket-wrapper-inline');
      if (form && form.offsetParent !== null) {
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        form.querySelector('input[type="email"], input[type="tel"]')?.focus({ preventScroll: true });
        return;
      }
      if (performance.now() - started < 2500) requestAnimationFrame(look);
    };
    look();
  }

  /**
   * Resolves once the form's variant id changes, or after a short ceiling so a
   * stalled update can never leave the shopper with a dead button.
   * @param {HTMLInputElement|HTMLSelectElement} input
   * @param {string|undefined} previous
   * @param {number} timeout
   * @returns {Promise<void>}
   */
  #awaitVariant(input, previous, timeout = 600) {
    return new Promise((resolve) => {
      const started = performance.now();
      const poll = () => {
        if (input.value !== previous || performance.now() - started > timeout) {
          resolve();
          return;
        }
        requestAnimationFrame(poll);
      };
      poll();
    });
  }
}

if (!customElements.get('size-sheet')) {
  customElements.define('size-sheet', SizeSheet);
}
